import React from "react";
import { Card, Container, Button, Table, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <Container className="py-4">
      <Card>
        <Card.Body className="p-4">
          <h1 className="mb-4">Terms and Conditions for QuVikme</h1>

          <Alert variant="info" className="mb-4">
            <strong>Effective Date:</strong> December 21, 2025
            <br />
            <strong>Last Updated:</strong> December 21, 2025
          </Alert>

          <Alert variant="warning" className="mb-4">
            <strong>Important:</strong> Please read these Terms and Conditions
            carefully before using the QuVikme mobile application. By
            downloading, installing, accessing, or using the QuVikme app, you
            agree to be bound by these Terms and Conditions. If you do not agree
            to these terms, please do not use our app.
          </Alert>

          <section className="mb-4">
            <h2 className="h4 mb-3">1. Acceptance of Terms</h2>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding
              agreement between you and QuVikme ("we," "us," or "our") governing
              your use of the QuVikme mobile application ("App") and related
              services ("Services"). By creating an account or using our App,
              you confirm that:
            </p>
            <ul>
              <li>You have read, understood, and agree to these Terms</li>
              <li>You are at least 18 years of age</li>
              <li>You have the legal capacity to enter into this agreement</li>
              <li>All information you provide is accurate and complete</li>
              <li>You will comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">2. Service Description</h2>

            <h3 className="h5 mb-2">2.1 About QuVikme</h3>
            <p>
              QuVikme is a comprehensive service marketplace platform that
              connects customers ("Users") with local service providers
              ("Vendors"). The platform provides:
            </p>
            <ul>
              <li>Service discovery and vendor browsing</li>
              <li>Service request and booking management</li>
              <li>Location-based vendor matching (10km radius)</li>
              <li>Communication tools between users and vendors</li>
              <li>Secure payment processing</li>
              <li>Review and rating system</li>
              <li>Subscription management</li>
              <li>Multi-language support (English, Hindi, Marathi)</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">2.2 Platform Nature</h3>
            <p>
              <strong>QuVikme is a marketplace platform only.</strong> We
              facilitate connections between users and vendors but are not
              responsible for:
            </p>
            <ul>
              <li>The quality, safety, or legality of services provided</li>
              <li>The accuracy of vendor listings or information</li>
              <li>The ability of vendors to complete services</li>
              <li>Disputes between users and vendors</li>
              <li>
                Any damages, injuries, or losses arising from service delivery
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">2.3 Geographic Availability</h3>
            <p>
              QuVikme services are currently available only in India. The
              10-kilometer radius matching system applies to all service
              requests.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">
              3. Account Registration and Requirements
            </h2>

            <h3 className="h5 mb-2">3.1 For All Users</h3>
            <p>To use our Service, you must:</p>
            <ul>
              <li>Be at least 18 years of age</li>
              <li>
                Provide accurate, current, and complete information during
                registration
              </li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Have a valid email address and phone number</li>
              <li>Create a secure password and keep it confidential</li>
              <li>
                Accept responsibility for all activities under your account
              </li>
              <li>Immediately notify us of any unauthorized account access</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">3.2 Vendor Requirements</h3>
            <p>In addition to general requirements, vendors must:</p>
            <ul>
              <li>Provide complete and accurate business information</li>
              <li>Submit valid business certificates and documentation</li>
              <li>Complete KYC (Know Your Customer) verification</li>
              <li>
                Maintain professional liability insurance (where applicable)
              </li>
              <li>
                Possess all necessary licenses and permits for their services
              </li>
              <li>
                Provide accurate location information for service area matching
              </li>
              <li>
                Maintain an active subscription to receive service requests
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">3.3 Account Security</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any security breach</li>
              <li>Ensuring no unauthorized access to your account</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">4. Subscription Terms</h2>

            <h3 className="h5 mb-2">4.1 Subscription Requirement</h3>
            <p>Active subscriptions are required for:</p>
            <ul>
              <li>
                <strong>Vendors:</strong> To receive and accept service requests
              </li>
              <li>
                <strong>Users:</strong> To create service requests and book
                vendors
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">4.2 Grace Period</h3>
            <ul>
              <li>
                Both users and vendors receive a 45-day grace period after
                subscription expiration
              </li>
              <li>
                Daily notifications will be sent 15 days before subscription
                expiration
              </li>
              <li>During the grace period, functionality may be limited</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">
              4.3 Subscription Expiration Effects
            </h3>
            <p>
              <strong>For Vendors:</strong>
            </p>
            <ul>
              <li>Cannot view new service requests</li>
              <li>Cannot accept new service requests</li>
              <li>Existing bookings can be completed</li>
            </ul>

            <p>
              <strong>For Users:</strong>
            </p>
            <ul>
              <li>Cannot create new service requests</li>
              <li>Cannot book new vendors</li>
              <li>Existing bookings can be completed</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">4.4 Renewal and Cancellation</h3>
            <ul>
              <li>Subscriptions can be renewed at any time through the app</li>

              <li>
                Subscription cancellation will take effect at the end of the
                current billing period.
              </li>
              <li>
                Users and vendors will continue to have access to subscription
                benefits until the subscription period expires.
              </li>
              <li>
                <strong>No refunds are provided for subscription fees</strong>,
                including but not limited to:
                <ul>
                  <li>Partial subscription periods</li>
                  <li>Unused time</li>
                  <li>Early cancellation</li>
                </ul>
              </li>
              <li>Subscription fees are non-refundable once charged.</li>
            </ul>

            <p>
              By purchasing a subscription, users and vendors acknowledge and
              agree to this cancellation and no-refund policy.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">5. Booking System and Service Requests</h2>

            <h3 className="h5 mb-2">5.1 User Service Requests</h3>
            <p>
              <strong>Users can:</strong>
            </p>
            <ul>
              <li>Browse vendors by category or service name</li>
              <li>
                Create service requests with requirements and budget range
              </li>
              <li>Set preferred date, time, and service address</li>
              <li>Have a maximum of 3 in-progress bookings at any time</li>
              <li>Cancel or adjust pricing before vendor acceptance</li>
            </ul>

            <p>
              <strong>Service Request Process:</strong>
            </p>
            <ol>
              <li>User creates service request with details</li>
              <li>System validates active subscription and booking limit</li>
              <li>Request is sent to verified vendors within 10km radius</li>
              <li>
                Status changes: pending → in_progress → completed/cancelled
              </li>
            </ol>

            <h3 className="h5 mb-2 mt-3">5.2 Vendor Service Acceptance</h3>
            <p>
              <strong>Vendors can:</strong>
            </p>
            <ul>
              <li>
                View service requests matching their category and location
                (within 10km)
              </li>
              <li>Accept pending service requests</li>
              <li>Maintain a maximum of 10 in-progress bookings</li>
              <li>Communicate with customers through messaging or calling</li>
              <li>Update booking status as work progresses</li>
            </ul>

            <p>
              <strong>Vendor Matching Criteria:</strong>
            </p>
            <ul>
              <li>Service category match</li>
              <li>Location proximity (within 10km radius)</li>
              <li>
                Vendor verification status (only verified vendors receive
                requests)
              </li>
              <li>Vendor activity status (recently active)</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">5.3 Booking Limits</h3>
            <Table striped bordered hover responsive className="mt-3">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Maximum In-Progress Bookings</th>
                  <th>Consequence of Exceeding Limit</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>User</td>
                  <td>3</td>
                  <td>Cannot create new service requests</td>
                </tr>
                <tr>
                  <td>Vendor</td>
                  <td>10</td>
                  <td>Cannot accept new service requests</td>
                </tr>
              </tbody>
            </Table>

            <h3 className="h5 mb-2 mt-3">5.4 Cancellation Policy</h3>
            <p>
              <strong>Users can cancel:</strong>
            </p>
            <ul>
              <li>Before vendor acceptance: Free cancellation</li>
              <li>After vendor acceptance: Subject to cancellation fees</li>
              <li>Cancellation must be done through the app</li>
            </ul>

            <p>
              <strong>Vendors can cancel:</strong>
            </p>
            <ul>
              <li>Only with valid reason and customer notification</li>
              <li>Excessive cancellations may affect vendor rating</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">6. Payment Terms</h2>

            <h3 className="h5 mb-2">6.1 Payment Processing</h3>
            <ul>
              <li>
                All payments are processed through Razorpay, our secure payment
                partner
              </li>
              <li>
                We accept credit cards, debit cards, UPI, and other payment
                methods supported by Razorpay
              </li>
              <li>Payment information is encrypted and handled by Razorpay</li>
              <li>QuVikme does not store complete payment card information</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">6.2 Pricing and Fees</h3>
            <p>
              <strong>For Users:</strong>
            </p>
            <ul>
              <li>Service prices are set by individual vendors</li>
              <li>GST and service tax are added to the total amount</li>
              <li>Users see the final amount before confirming booking</li>
              <li>Budget ranges can be specified in service requests</li>
            </ul>

            <p>
              <strong>For Vendors:</strong>
            </p>
            <ul>
              <li>Platform fees are deducted from earnings</li>
              <li>Service tax applies to vendor earnings</li>
              <li>Vendors set their own service pricing</li>
              <li>Platform fees are disclosed transparently</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">6.3 Refund Policy</h3>
            <ul>
              <li>Refunds are processed on a case-by-case basis</li>
              <li>
                Disputes must be reported within 7 days of service completion
              </li>
              <li>Refunds are processed through the original payment method</li>
              <li>Processing time: 5-10 business days</li>
              <li>Subscription fees are non-refundable</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">6.4 Vendor Withdrawals</h3>
            <p>
              <strong>Withdrawal Requirements:</strong>
            </p>
            <ul>
              <li>Minimum withdrawal amount: 500 INR</li>
              <li>KYC verification must be completed before withdrawals</li>
              <li>Valid bank account or UPI details required</li>
              <li>Withdrawal requests are subject to admin approval</li>
            </ul>

            <p>
              <strong>Withdrawal Process:</strong>
            </p>
            <ol>
              <li>
                Vendor opens withdrawal page and enters amount (minimum 500 INR)
              </li>
              <li>System checks KYC verification status</li>
              <li>Withdrawal request is created with "Pending" status</li>
              <li>Admin reviews vendor KYC and bank details</li>
              <li>Admin approves or rejects the request</li>
              <li>Approved payments are processed within 3-5 business days</li>
              <li>Vendor receives notification of status update</li>
            </ol>

            <p>
              <strong>Withdrawal Status:</strong>
            </p>
            <ul>
              <li>
                <strong>Pending:</strong> Under review by admin
              </li>
              <li>
                <strong>Completed:</strong> Payment processed successfully
              </li>
              <li>
                <strong>Rejected:</strong> Request denied (vendor notified with
                reason)
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">7. KYC Verification</h2>

            <h3 className="h5 mb-2">7.1 Vendor Verification Process</h3>
            <p>All vendors must complete KYC verification:</p>
            <ol>
              <li>Vendor registers and creates profile</li>
              <li>
                Vendor uploads required documents:
                <ul>
                  <li>Government-issued photo ID</li>
                  <li>Address proof</li>
                  <li>Business certificate/license</li>
                  <li>Bank account details</li>
                </ul>
              </li>
              <li>Vendor submits verification request</li>
              <li>Admin reviews documents</li>
              <li>Admin accepts or rejects verification</li>
              <li>Verified vendors can operate and receive service requests</li>
            </ol>

            <h3 className="h5 mb-2 mt-3">
              7.2 KYC Requirements for Withdrawals
            </h3>
            <ul>
              <li>
                Vendors cannot withdraw earnings without completed KYC
                verification
              </li>
              <li>If KYC is rejected, vendor must fix issues and resubmit</li>
              <li>Admin provides reason for rejection</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">7.3 Document Authenticity</h3>
            <p>You warrant that:</p>
            <ul>
              <li>All submitted documents are genuine and unaltered</li>
              <li>Information provided is accurate and current</li>
              <li>You have the legal right to provide the documents</li>
              <li>False information may result in account termination</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">8. Referral Program</h2>

            <h3 className="h5 mb-2">8.1 Referral System</h3>
            <ul>
              <li>
                Both users and vendors receive unique referral codes upon
                registration
              </li>
              <li>
                Referral codes can be shared via social media, messaging apps,
                or other means
              </li>
              <li>
                Successful referrals earn 10 points (equivalent to 10 INR)
              </li>
              <li>
                Points are awarded after successful registration and
                verification completion
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">8.2 Referral Terms</h3>
            <ul>
              <li>Referral rewards are valid only for new users/vendors</li>
              <li>
                Self-referrals are prohibited and will result in point
                forfeiture
              </li>
              <li>
                Fraudulent referral activity may lead to account suspension
              </li>
              <li>Referral points can be used according to program terms</li>
              <li>
                We reserve the right to modify or terminate the referral program
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">8.3 Point Redemption</h3>
            <ul>
              <li>
                Points can be redeemed as specified in the referral program
              </li>
              <li>Minimum redemption threshold may apply</li>
              <li>Points have no cash value outside the QuVikme platform</li>
              <li>Points may expire according to program terms</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">9. Advertising System</h2>

            <h3 className="h5 mb-2">9.1 Vendor Advertisements</h3>
            <p>
              <strong>Vendors can:</strong>
            </p>
            <ul>
              <li>Create banner advertisements for their services</li>
              <li>
                Select category, add title, description, price/offer, and images
              </li>
              <li>Preview advertisements before publishing</li>
              <li>Delete previously created advertisements</li>
              <li>Gain increased visibility among users</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">9.2 Advertisement Approval</h3>
            <ul>
              <li>
                All advertisements require admin approval before being displayed
              </li>
              <li>Admin reviews ads for policy compliance and quality</li>
              <li>Rejected ads are not shown to users</li>
              <li>Vendors are notified of approval or rejection status</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">9.3 Advertisement Guidelines</h3>
            <p>Advertisements must not:</p>
            <ul>
              <li>Contain false, misleading, or deceptive information</li>
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Contain offensive, inappropriate, or illegal content</li>
              <li>Promote prohibited services</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">9.4 For Users</h3>
            <ul>
              <li>Approved ads are displayed in user dashboard</li>
              <li>Users can click ads to view full details</li>
              <li>Users cannot remove or block individual ads</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">10. Reviews and Ratings</h2>

            <h3 className="h5 mb-2">10.1 Review System</h3>
            <p>Users can:</p>
            <ul>
              <li>Rate vendors after service completion (1-5 stars)</li>
              <li>Write detailed reviews describing their experience</li>
              <li>Provide feedback to admin about platform issues</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">10.2 Review Guidelines</h3>
            <p>Reviews must:</p>
            <ul>
              <li>Be based on actual service experience</li>
              <li>Be honest, fair, and factual</li>
              <li>Not contain offensive language or personal attacks</li>
              <li>Not include promotional content or spam</li>
              <li>Not violate privacy or confidentiality</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">10.3 Review Moderation</h3>
            <ul>
              <li>
                We reserve the right to remove reviews that violate guidelines
              </li>
              <li>Reviews may be investigated for authenticity</li>
              <li>Fake or fraudulent reviews will be removed</li>
              <li>
                Vendors cannot remove negative reviews (unless they violate
                guidelines)
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">
              11. Administrative Rights and Multi-Tier System
            </h2>

            <h3 className="h5 mb-2">11.1 Admin Hierarchy</h3>
            <p>QuVikme operates a three-tier administrative system:</p>

            <Table striped bordered hover responsive className="mt-3">
              <thead>
                <tr>
                  <th>Admin Level</th>
                  <th>Access Scope</th>
                  <th>Key Permissions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Super Admin</td>
                  <td>Full platform access</td>
                  <td>All features, all data, create/manage sub-admins</td>
                </tr>
                <tr>
                  <td>Sub-Admin</td>
                  <td>Pincode-filtered access</td>
                  <td>Manage assigned area, create sub-sub admins</td>
                </tr>
                <tr>
                  <td>Sub-Sub Admin</td>
                  <td>Society-filtered access</td>
                  <td>Manage assigned society only</td>
                </tr>
              </tbody>
            </Table>

            <h3 className="h5 mb-2 mt-3">11.2 Admin Capabilities</h3>
            <p>Administrators can:</p>
            <ul>
              <li>Review and approve/reject vendor verification requests</li>
              <li>Review and approve/reject KYC documents</li>
              <li>Review and approve/reject vendor advertisements</li>
              <li>Manage subscription plans and features</li>
              <li>Process withdrawal requests</li>
              <li>Access vendor and user analytics (within their scope)</li>
              <li>Configure platform taxes and fees</li>
              <li>Suspend or terminate accounts for policy violations</li>
              <li>Resolve disputes between users and vendors</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">11.3 Admin Data Access</h3>
            <ul>
              <li>
                Admins can access profile information, bookings, transactions,
                and KYC documents within their authorized scope
              </li>
              <li>Admin access is subject to confidentiality obligations</li>
              <li>Admin actions are logged for audit purposes</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">12. Prohibited Activities</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Provide false, misleading, or inaccurate information</li>
              <li>Impersonate any person or entity</li>
              <li>
                Create multiple accounts to abuse referral or other systems
              </li>
              <li>Engage in fraudulent transactions or payment disputes</li>
              <li>Circumvent verification or security measures</li>
              <li>Share your account credentials with others</li>
              <li>Use automated systems (bots) to access the platform</li>
              <li>Scrape, data mine, or extract data from the platform</li>
              <li>Interfere with or disrupt the platform's operation</li>
              <li>Transmit malware, viruses, or harmful code</li>
              <li>Harass, abuse, or harm other users or vendors</li>
              <li>Post offensive, discriminatory, or illegal content</li>
              <li>Use the platform for unauthorized commercial purposes</li>
              <li>Manipulate reviews or ratings</li>
              <li>Solicit personal information from other users</li>
              <li>Complete transactions outside the platform to avoid fees</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">13. Intellectual Property Rights</h2>

            <h3 className="h5 mb-2">13.1 QuVikme Property</h3>
            <p>
              All content, features, and functionality of the QuVikme app,
              including but not limited to:
            </p>
            <ul>
              <li>Software code, design, and architecture</li>
              <li>QuVikme logo, trademarks, and branding</li>
              <li>Text, graphics, images, and user interface</li>
              <li>Algorithms and matching systems</li>
            </ul>
            <p>
              are owned by QuVikme and protected by copyright, trademark, and
              other intellectual property laws.
            </p>

            <h3 className="h5 mb-2 mt-3">13.2 User-Generated Content</h3>
            <p>When you post content (reviews, photos, messages) on QuVikme:</p>
            <ul>
              <li>You retain ownership of your content</li>
              <li>
                You grant QuVikme a worldwide, non-exclusive, royalty-free
                license to use, display, and distribute your content
              </li>
              <li>You warrant that you have the right to post the content</li>
              <li>
                You agree not to post content that infringes on others' rights
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">13.3 Vendor Content</h3>
            <p>Vendors grant QuVikme the right to:</p>
            <ul>
              <li>Display business information, services, and pricing</li>
              <li>Use portfolio images and work samples</li>
              <li>Share reviews and ratings</li>
              <li>Promote vendor services on the platform</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">13.4 Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>Copy, modify, or create derivative works of the app</li>
              <li>Reverse engineer or decompile the software</li>
              <li>Use QuVikme branding without permission</li>
              <li>Remove or alter copyright notices</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">
              14. Disclaimers and Limitation of Liability
            </h2>

            <h3 className="h5 mb-2">14.1 Platform Disclaimer</h3>
            <Alert variant="danger">
              <strong>IMPORTANT DISCLAIMER:</strong>
              <p className="mb-2">
                QuVikme is a marketplace platform that connects users with
                vendors. We do not provide services directly and are not
                responsible for:
              </p>
              <ul className="mb-0">
                <li>
                  The quality, safety, timeliness, or legality of services
                  provided by vendors
                </li>
                <li>The actions, conduct, or qualifications of vendors</li>
                <li>Disputes between users and vendors</li>
                <li>
                  Damages, injuries, or losses resulting from service delivery
                </li>
                <li>The accuracy of vendor listings or information</li>
              </ul>
            </Alert>

            <h3 className="h5 mb-2 mt-3">14.2 "As Is" Service</h3>
            <p>
              THE APP AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE"
              WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Merchantability, fitness for a particular purpose</li>
              <li>Non-infringement</li>
              <li>Uninterrupted or error-free operation</li>
              <li>Accuracy or reliability of information</li>
              <li>Security of data transmission</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">14.3 Limitation of Liability</h3>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul>
              <li>
                QuVikme shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages
              </li>
              <li>
                Our total liability for any claim shall not exceed the amount
                paid by you in the past 12 months, or 5,000 INR, whichever is
                less
              </li>
              <li>
                We are not liable for service provider negligence, errors, or
                omissions
              </li>
              <li>
                We are not liable for losses due to system failures,
                interruptions, or data loss
              </li>
            </ul>

            <h3 className="h5 mb-2 mt-3">14.4 Third-Party Services</h3>
            <ul>
              <li>
                We use third-party services (MongoDB Cloud, Razorpay, Google Maps)
                that have their own terms
              </li>
              <li>
                We are not responsible for third-party service failures or
                policies
              </li>
              <li>
                Links to third-party websites are provided for convenience only
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">15. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless QuVikme, its
              officers, directors, employees, and agents from any claims,
              damages, losses, liabilities, and expenses (including legal fees)
              arising from:
            </p>
            <ul>
              <li>Your use or misuse of the platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any laws or third-party rights</li>
              <li>Content you post or transmit</li>
              <li>Your interaction with other users or vendors</li>
              <li>Services provided or received through the platform</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">16. Account Termination</h2>

            <h3 className="h5 mb-2">16.1 Termination by You</h3>
            <p>You may terminate your account at any time by:</p>
            <ul>
              <li>Going to Settings &gt; Account &gt; Delete Account</li>
              <li>Contacting customer support</li>
            </ul>

            <p>Upon termination:</p>
            <ul>
              <li>Your access to the platform will be revoked</li>
              <li>
                Your personal data will be deleted (except as required by law)
              </li>
              <li>Outstanding payment obligations remain due</li>
              <li>Completed bookings cannot be reversed</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">16.2 Termination by Us</h3>
            <p>We may suspend or terminate your account immediately if:</p>
            <ul>
              <li>You violate these Terms and Conditions</li>
              <li>You engage in fraudulent or illegal activities</li>
              <li>You provide false information during verification</li>
              <li>You abuse the referral or rating system</li>
              <li>You repeatedly violate platform policies</li>
              <li>We are required to do so by law</li>
              <li>Your account poses security risks</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">16.3 Effect of Termination</h3>
            <ul>
              <li>All licenses granted to you will immediately terminate</li>
              <li>You must cease all use of the platform</li>
              <li>
                Provisions that should survive (payment obligations,
                indemnification) remain in effect
              </li>
              <li>We may retain certain information as required by law</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">17. Dispute Resolution</h2>

            <h3 className="h5 mb-2">17.1 User-Vendor Disputes</h3>
            <p>For disputes between users and vendors:</p>
            <ul>
              <li>Parties should first attempt to resolve directly</li>
              <li>Disputes can be reported through the app within 7 days</li>
              <li>QuVikme may mediate but is not obligated to do so</li>
              <li>Our decision in mediation is final and binding</li>
            </ul>

            <h3 className="h5 mb-2 mt-3">17.2 Governing Law</h3>
            <p>
              These Terms are governed by the laws of India. Any disputes shall
              be subject to the exclusive jurisdiction of the courts in [Your
              City, State], India.
            </p>

            <h3 className="h5 mb-2 mt-3">17.3 Arbitration (Optional)</h3>
            <p>
              For disputes exceeding 50,000 INR, parties may agree to binding
              arbitration under the Arbitration and Conciliation Act, 1996.
              Arbitration shall be conducted in [Your City], India.
            </p>

            <h3 className="h5 mb-2 mt-3">17.4 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes individually and waive the right to
              participate in class action lawsuits.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">18. Force Majeure</h2>
            <p>
              QuVikme shall not be liable for any failure or delay in
              performance due to circumstances beyond our reasonable control,
              including:
            </p>
            <ul>
              <li>Natural disasters (earthquakes, floods, storms)</li>
              <li>Pandemics or public health emergencies</li>
              <li>War, terrorism, or civil unrest</li>
              <li>Government actions or regulations</li>
              <li>Power outages or telecommunications failures</li>
              <li>Internet service provider failures</li>
              <li>Third-party service disruptions</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">19. Privacy and Data Protection</h2>
            <p>
              Your use of the QuVikme app is also governed by our Privacy
              Policy, which is incorporated into these Terms by reference.
              Please review our Privacy Policy to understand:
            </p>
            <ul>
              <li>What information we collect</li>
              <li>How we use and share your information</li>
              <li>Your privacy rights and choices</li>
              <li>Our security practices</li>
            </ul>
            <p>
              Privacy Policy:{" "}
              <a
                href="https://admin.quvikme.com/privacy-policy"
                className="text-primary"
              >
                https://admin.quvikme.com/privacy-policy
              </a>
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">20. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we
              make changes:
            </p>
            <ul>
              <li>We will update the "Last Updated" date</li>
              <li>
                We will notify you through the app or via email for material
                changes
              </li>
              <li>
                You will have at least 30 days to review changes before they
                take effect
              </li>
              <li>Continued use after changes constitutes acceptance</li>
              <li>If you disagree with changes, you must stop using the app</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">21. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid, illegal,
              or unenforceable:
            </p>
            <ul>
              <li>
                The remaining provisions shall continue in full force and effect
              </li>
              <li>
                The invalid provision shall be modified to the minimum extent
                necessary to make it valid
              </li>
              <li>The overall intent of these Terms shall be preserved</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">22. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any other
              policies referenced herein, constitute the entire agreement
              between you and QuVikme regarding the use of the platform and
              supersede all prior agreements and understandings.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">23. Assignment</h2>
            <ul>
              <li>
                You may not assign or transfer these Terms without our written
                consent
              </li>
              <li>We may assign these Terms to any successor entity</li>
              <li>Any unauthorized assignment is void</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">24. Waiver</h2>
            <p>
              Our failure to enforce any provision of these Terms shall not be
              construed as a waiver of that provision or our right to enforce it
              in the future.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">25. Language Support</h2>
            <p>
              QuVikme supports multiple languages (English, Hindi, Marathi). In
              case of any discrepancy between translated versions, the English
              version shall prevail.
            </p>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">26. Contact Information</h2>
            <Alert variant="success">
              <p className="mb-2">
                For questions, concerns, or support regarding these Terms and
                Conditions, please contact us:
              </p>

              <p className="mb-2">
                <strong>QuVikme</strong>
                <br />
                Email:{" "}
                <a href="mailto:support@quvikme.com">support@quvikme.com</a>
                <br />
                Legal Email:{" "}
                <a href="mailto:legal@quvikme.com">legal@quvikme.com</a>
                <br />
                Address: [Your Complete Business Address]
                <br />
                Phone: [Your Contact Number]
                <br />
                Business Hours: Monday - Friday, 9:00 AM - 6:00 PM IST
              </p>

              <p className="mb-0">
                <strong>Customer Support:</strong>
                <br />
                For booking issues, payment problems, or general inquiries,
                please use the in-app support feature or email
                support@quvikme.com
              </p>
            </Alert>
          </section>

          <section className="mb-4">
            <h2 className="h4 mb-3">27. Acknowledgment</h2>
            <p>BY USING THE QuVikme APP, YOU ACKNOWLEDGE THAT:</p>
            <ul>
              <li>You have read and understood these Terms and Conditions</li>
              <li>You agree to be bound by these Terms</li>
              <li>You meet all eligibility requirements</li>
              <li>
                You understand QuVikme is a marketplace platform, not a service
                provider
              </li>
              <li>You accept the risks associated with using the platform</li>
              <li>You have read and agree to the Privacy Policy</li>
            </ul>
          </section>

          <div className="text-center mt-5 pt-4 border-top text-muted">
            <p className="mb-1">© 2025 QuVikme. All rights reserved.</p>
            <p className="small">
              These Terms and Conditions are effective as of December 21, 2025
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TermsAndConditions;
